// @ts-nocheck

import React from 'react';
import {
  Slider,
  SliderProps,
  Tooltip,
  tooltipClasses,
  styled,
  Typography,
  Box,
  InputBase,
  Grid,
  InputAdornment,
} from '@mui/material';
import styles from '@/components/atoms/Slider/slider.module.css';

// Custom styled input
const StyledInput = styled(InputBase)(({ theme }) => ({
  width: '80px', // Increased width to accommodate postfix
  '& .MuiInputBase-input': {
    textAlign: 'left', // Right align for better postfix appearance
    padding: '4px 8px !important',
    fontSize: '14px',
    borderRadius: 4,
    border: '1px solid var(--main)',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
    },
    '&:hover': {
      borderColor: 'var(--main-dark)',
    },
    '&:focus': {
      borderColor: 'var(--main-dark)',
      borderWidth: 2,
    },
  },
  '& .MuiInputAdornment-positionEnd': {
    marginLeft: 0,
    paddingRight: 0,
    paddingLeft: 8,
  },
}));

// Custom Tooltip component
const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'var(--main)',
    color: '#fff',
    fontSize: 12,
    padding: '6px 12px',
    borderRadius: 4,
  },
}));

interface Mark {
  value: number;
  label?: React.ReactNode;
}

interface BaseAtomSliderProps {
  value: number | number[];
  onChange?: (event: Event | React.SyntheticEvent, newValue: number | number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string | React.ReactNode;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  size?: 'small' | 'medium';
  showValueLabel?: boolean;
  customTooltip?: boolean;
  tooltipContent?: (value: number) => React.ReactNode;
  showInput?: boolean;
  inputPostfix?: string; // New prop for input postfix
  className?: string;
}

type AtomSliderProps = BaseAtomSliderProps & Omit<SliderProps, keyof BaseAtomSliderProps>;

const AtomSlider: React.FC<AtomSliderProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  color = 'primary',
  size = 'medium',
  showValueLabel = false,
  customTooltip = false,
  tooltipContent,
  showInput = false,
  inputPostfix,
  className = '',
  ...props
}) => {
  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (onChange) {
      onChange(event, newValue);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value === '' ? min : Number(event.target.value);

    if (onChange) {
      const syntheticEvent = { target: { value: newValue } } as unknown as Event;
      onChange(syntheticEvent, newValue);
    }
  };

  const handleBlur = () => {
    let newValue = value as number;
    if (newValue < min) {
      newValue = min;
    } else if (newValue > max) {
      newValue = max;
    }

    if (onChange) {
      const syntheticEvent = { target: { value: newValue } } as unknown as Event;
      onChange(syntheticEvent, newValue);
    }
  };

  const baseClassName = `${styles.atomSlider} ${styles[color]} ${styles[size]} ${className}`;

  const renderSlider = () => (
    <Slider
      value={value}
      onChange={handleSliderChange}
      min={min}
      max={max}
      step={step}
      className={baseClassName}
      classes={{
        root: styles.root,
        rail: styles.rail,
        track: styles.track,
        thumb: styles.thumb,
        valueLabel: styles.valueLabel,
        mark: styles.mark,
        markLabel: styles.markLabel,
        active: styles.active,
        disabled: styles.disabled,
      }}
      {...props}
    />
  );

  const sliderComponent = showInput ? (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs>
        {renderSlider()}
      </Grid>
      <Grid item>
        <StyledInput
          value={typeof value === 'number' ? value : value[0]}
          onChange={handleInputChange}
          onBlur={handleBlur}
          endAdornment={
            inputPostfix ? <InputAdornment position="end">{inputPostfix}</InputAdornment> : null
          }
          inputProps={{
            step: step,
            min: min,
            max: max,
            type: 'number',
            'aria-labelledby': 'input-slider',
            style: { paddingRight: inputPostfix ? '32px' : '8px' },
          }}
        />
      </Grid>
    </Grid>
  ) : (
    renderSlider()
  );

  return (
    <Box className={styles.sliderContainer}>
      {label && (
        <Typography variant="body2" className={styles.sliderLabel}>
          {label}
        </Typography>
      )}

      <Box className={styles.sliderContent}>
        {customTooltip ? (
          <CustomTooltip
            title={tooltipContent ? tooltipContent(value as number) : value}
            placement="top"
            arrow
          >
            {sliderComponent}
          </CustomTooltip>
        ) : (
          sliderComponent
        )}

        {showValueLabel && !customTooltip && !showInput && (
          <Typography variant="body2" className={styles.valueDisplay}>
            {Array.isArray(value) ? `${value[0]} - ${value[1]}` : value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AtomSlider;
